import { CARD_API_ENDPOINT } from '@/config/consts'
import { useQuery } from '@tanstack/react-query'
import { SOLVER_DISPLAY_MAP, type SolverType } from '../constants'
import { createSmartGet } from './useAuthenticatedMutation'

/** Shape returned by GET /v1/vendor/cards/solvers */
export interface SolverInfo {
  id: SolverType | string
  mode: 'borrow' | 'spend'
  name: string
  comingSoon?: boolean
}

interface SolversResponse {
  data: SolverInfo[]
}

/**
 * Fetches available solver types from the backend.
 *
 * Falls back to SOLVER_DISPLAY_MAP entries if the API returns empty or errors.
 */
export function useSolvers() {
  const url = `${CARD_API_ENDPOINT}/v1/vendor/cards/solvers`
  const smartGet = createSmartGet(url)

  const { data, isLoading, error } = useQuery({
    queryKey: ['card-solvers'],
    queryFn: () => smartGet() as Promise<SolversResponse>,
    staleTime: 1_800_000,
    gcTime: 3_600_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const rawSolvers = Array.isArray(data?.data) ? data.data : []

  const fallback: SolverInfo[] = Object.entries(SOLVER_DISPLAY_MAP).map(([key, info]) => ({
    id: key,
    mode: info.mode,
    name: info.displayName,
  }))

  const solvers: SolverInfo[] = rawSolvers.length > 0 ? rawSolvers : fallback

  return {
    solvers,
    borrowSolvers: solvers.filter((s) => s.mode === 'borrow'),
    spendSolvers: solvers.filter((s) => s.mode === 'spend'),
    isLoading,
    error,
  }
}
